import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Paper,
  Grid,
  Button,
  IconButton,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import Ads2Component from "../ads/Ads2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NewspaperIcon from "@mui/icons-material/Newspaper";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: "#0288D1", light: "#e0f2fe", dark: "#0277bd" },
    background: { default: "#F0F6FF", paper: "#ffffff" },
    text: { primary: "#0c1a2e", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Bricolage Grotesque', 'Sora', sans-serif",
    h2: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 900 },
    h3: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 900 },
    h4: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const HeroBar = styled(Box)(() => ({
  background: "linear-gradient(135deg, #023e8a 0%, #0288D1 50%, #0ea5e9 100%)",
  padding: "72px 24px 100px",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    opacity: 0.05,
    backgroundImage:
      "radial-gradient(circle at 25% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 50%, #fff 1px, transparent 1px)",
    backgroundSize: "60px 60px",
  },
}));

const HeroBackBtn = styled(IconButton)(() => ({
  position: "absolute",
  left: 24,
  top: "50%",
  transform: "translateY(-50%)",
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.25)",
  color: "#fff",
  zIndex: 10,
  borderRadius: "12px",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.25)",
    transform: "translateY(-50%) translateX(-2px)",
  },
}));

const SearchWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  "& .search-icon": {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 18,
    color: "#9CA3AF",
    pointerEvents: "none",
  },
}));

const SearchInput = styled("input")(({ theme }) => ({
  width: "100%",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  fontSize: 15,
  color: "#0c1a2e",
  background: alpha("#F0F6FF", 0.7),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 12,
  padding: "13px 16px 13px 44px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#0288D1",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(2,136,209,0.1)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const CatBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  borderRadius: 10,
  padding: "8px 20px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  transition: "all 0.18s ease",
  ...(active
    ? {
        background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
        color: "#fff",
        border: "none",
        boxShadow: "0 4px 14px rgba(2,136,209,0.3)",
        "&:hover": {
          background: "linear-gradient(135deg, #0277bd, #0288D1)",
          transform: "translateY(-1px)",
        },
      }
    : {
        background: "transparent",
        color: "#4B5563",
        border: `1px solid ${alpha("#000", 0.12)}`,
        "&:hover": {
          background: alpha("#0288D1", 0.06),
          borderColor: alpha("#0288D1", 0.3),
          color: "#0288D1",
        },
      }),
}));

const NewsCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 18,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.05)",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  animation: `${cardEntrance} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.24s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 12px 36px rgba(2,136,209,0.1), 0 4px 12px rgba(0,0,0,0.07)",
    borderColor: alpha("#0288D1", 0.2),
    "& .news-img": { transform: "scale(1.04)" },
  },
}));

const NewsImgBox = styled(Box)(() => ({
  height: 210,
  overflow: "hidden",
  background: alpha("#0288D1", 0.06),
  position: "relative",
}));

const NewsImg = styled("img")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "transform 0.45s ease",
}));

const NewsImgPlaceholder = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${alpha("#0288D1", 0.06)}, ${alpha(
    "#0288D1",
    0.12
  )})`,
}));

const ReadMoreBtn = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  color: "#0288D1",
  padding: "4px 12px",
  background: alpha("#0288D1", 0.07),
  border: `1px solid ${alpha("#0288D1", 0.18)}`,
  transition: "all 0.18s ease",
  "&:hover": { background: "#0288D1", color: "#fff", borderColor: "#0288D1" },
}));

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 18,
  border: `1px solid ${alpha("#000", 0.06)}`,
  overflow: "hidden",
  background: "#fff",
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);

const FilterCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(2,136,209,0.05)",
  padding: "24px 28px",
  marginBottom: 40,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: "60ms",
}));

// ─── NewsItem Component ───────────────────────────────────────────────────────
const NewsItem = ({ news, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <NewsCard itemdelay={index * 60}>
      <NewsImgBox>
        {news.news_img ? (
          <NewsImg
            className="news-img"
            src={news.news_img}
            alt={news.title}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <NewsImgPlaceholder>
            <NewspaperIcon
              sx={{ fontSize: 40, color: "#0288D1", opacity: 0.3 }}
            />
          </NewsImgPlaceholder>
        )}

        {/* Category overlay */}
        {news.category_name && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              background: "rgba(2,46,109,0.82)",
              backdropFilter: "blur(8px)",
              borderRadius: "8px",
              px: 1.4,
              py: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                letterSpacing: "0.3px",
              }}
            >
              {news.category_name}
            </Typography>
          </Box>
        )}
      </NewsImgBox>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: "20px 22px",
        }}
      >
        {/* Time chip */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1.5 }}>
          <AccessTimeIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
          <Typography
            sx={{
              fontSize: 11,
              color: "#9CA3AF",
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontWeight: 600,
            }}
          >
            {moment(news.date).fromNow()}
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontWeight: 800,
            color: "#0c1a2e",
            lineHeight: 1.35,
            mb: 1.5,
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: "1.02rem",
            letterSpacing: "-0.2px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {news.title}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: "#4B5563",
            lineHeight: 1.75,
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontWeight: 400,
            display: "-webkit-box",
            WebkitLineClamp: expanded ? "unset" : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: 2,
            flex: 1,
          }}
        >
          {news.description}
        </Typography>

        {/* Read more */}
        <ReadMoreBtn
          size="small"
          endIcon={
            expanded ? (
              <ExpandLessIcon sx={{ fontSize: "14px !important" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: "14px !important" }} />
            )
          }
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? "Show Less" : "Read More"}
        </ReadMoreBtn>
      </Box>
    </NewsCard>
  );
};

// ─── Main NewsPage Component ──────────────────────────────────────────────────
const NewsPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/news/`);
        setCategories(res.data.categories);
        setNewsItems(res.data.all_news);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNewsData();
  }, []);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    if (categoryId === null) {
      axios
        .get(`${API_URL}/news/`)
        .then((res) => setNewsItems(res.data.all_news));
    } else {
      const category = categories.find((c) => c.id === categoryId);
      setNewsItems(category?.news_items || []);
    }
  };

  const filteredNews = newsItems.filter((n) =>
    n.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap"
        rel="stylesheet"
      />

      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(160deg, #f0f6ff 0%, #e8f4fd 50%, #f5faff 100%)",
          fontFamily: "'Bricolage Grotesque', sans-serif",
        }}
      >
        {/* ── Hero ── */}
        <HeroBar>
          <HeroBackBtn onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </HeroBackBtn>

          {/* Decorative blobs */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 280,
              height: 280,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -40,
              left: -40,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.03)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Chip
              label="📰 Education & Exam Insights"
              size="small"
              sx={{
                mb: 2.5,
                fontWeight: 700,
                fontSize: "0.74rem",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                letterSpacing: "0.4px",
                background: "rgba(255,255,255,0.14)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: "8px",
                backdropFilter: "blur(8px)",
              }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                color: "#fff",
                fontSize: { xs: "2.6rem", md: "4rem" },
                letterSpacing: "-1.5px",
                lineHeight: 1.08,
                fontFamily: "'Bricolage Grotesque', sans-serif",
                mb: 2,
              }}
            >
              Latest News
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "1.05rem",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 400,
              }}
            >
              Stay updated with education &amp; exam insights
            </Typography>
          </Box>
        </HeroBar>

        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 5, md: 8 },
            mt: { xs: -4, md: -6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* ── Filter Card ── */}
          <FilterCard elevation={0}>
            {/* Search */}
            <SearchWrapper sx={{ mb: 3 }}>
              <SearchIcon className="search-icon" />
              <SearchInput
                placeholder="Search news articles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapper>

            {/* Category filters */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              <CatBtn
                active={selectedCategoryId === null ? 1 : 0}
                onClick={() => handleCategoryChange(null)}
              >
                All News
              </CatBtn>
              {categories.map((cat) => (
                <CatBtn
                  key={cat.id}
                  active={selectedCategoryId === cat.id ? 1 : 0}
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  {cat.name}
                </CatBtn>
              ))}
            </Stack>
          </FilterCard>

          {/* ── Results count ── */}
          {!loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
                animation: `${fadeUp} .4s ease both`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#9CA3AF",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                {filteredNews.length} article
                {filteredNews.length !== 1 ? "s" : ""}
                {search && (
                  <span style={{ color: "#0288D1" }}> matching "{search}"</span>
                )}
              </Typography>
            </Box>
          )}

          {/* ── Skeleton ── */}
          {loading && (
            <Grid container spacing={3}>
              {[...Array(6)].map((_, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <SkeletonCard sx={{ animationDelay: `${i * 80}ms` }}>
                    <Box
                      sx={{ height: 210, background: alpha("#0288D1", 0.07) }}
                    />
                    <Box sx={{ p: "20px 22px" }}>
                      <SkeletonBlock w="30%" h={11} radius={5} />
                      <Box sx={{ mt: 1.5, mb: 1 }}>
                        <SkeletonBlock w="85%" h={18} radius={5} />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <SkeletonBlock w="60%" h={18} radius={5} />
                      </Box>
                      <SkeletonBlock w="100%" h={12} radius={5} />
                      <Box sx={{ mt: 1 }}>
                        <SkeletonBlock w="90%" h={12} radius={5} />
                      </Box>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <SkeletonBlock w="70%" h={12} radius={5} />
                      </Box>
                      <SkeletonBlock w={90} h={30} radius={8} />
                    </Box>
                  </SkeletonCard>
                </Grid>
              ))}
            </Grid>
          )}

          {/* ── News Grid ── */}
          {!loading && filteredNews.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 3,
                  background: alpha("#0288D1", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <NewspaperIcon
                  sx={{ fontSize: 28, color: "#0288D1", opacity: 0.45 }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#9CA3AF",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                {search ? `No articles match "${search}"` : "No news available"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  mt: 0.5,
                }}
              >
                {search
                  ? "Try a different search term"
                  : "Check back later for updates"}
              </Typography>
            </Box>
          )}

          {!loading && filteredNews.length > 0 && (
            <Grid container spacing={3}>
              {filteredNews.map((news, index) => (
                <React.Fragment key={news.id}>
                  <Grid item xs={12} sm={6}>
                    <NewsItem news={news} index={index} />
                  </Grid>
                  {index % 6 === 5 && (
                    <Grid item xs={12} key={`ad-${index}`}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          border: `1px solid ${alpha("#000", 0.06)}`,
                        }}
                      >
                        <Ads2Component />
                      </Box>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          )}
        </Container>

        <Box sx={{ py: 6 }} />
      </Box>
    </ThemeProvider>
  );
};

export default NewsPage;
